// Something a signed-out visitor tried to do to a listing, held while they go
// off to sign in, so it can be finished when they come back.
//
//   PENDING_ACTION_FAVORITE  the listing is saved as soon as the member loads
//                            (see src/ducks/favoriteListings.duck.js)
//   PENDING_ACTION_ENQUIRE   the listing page opens the application form once
//                            it is showing that listing again
//                            (see ListingPageCarousel.js)
//
// It is written only when the visitor presses "Sign in" in the sign-in prompt,
// not when the prompt opens. Someone who closes the prompt and signs in later
// from the top bar should not find a listing saved that they never meant to.
//
// sessionStorage, not localStorage: it belongs to this tab's sign-in round trip
// (which includes the IdP redirects), and should not follow the visitor into a
// new session days later. It is also cleared on logout (src/reducers.js).
// The age limit covers a visitor who starts signing in and wanders off.

const STORAGE_KEY = 'pendingListingAction';
const MAX_AGE_MS = 30 * 60 * 1000;

export const PENDING_ACTION_FAVORITE = 'favorite';
export const PENDING_ACTION_ENQUIRE = 'enquire';

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (e) {
    // Blocked storage (some private modes) throws on access.
    return null;
  }
};

const read = () => {
  const store = storage();
  if (!store) {
    return null;
  }
  try {
    const raw = store.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const clear = () => {
  const store = storage();
  if (!store) {
    return;
  }
  try {
    store.removeItem(STORAGE_KEY);
  } catch (e) {
    // Nothing to do: the entry expires on its own.
  }
};

/**
 * Remember an action to finish after sign-in. Replaces any earlier one: only
 * the last thing the visitor asked for is resumed.
 */
export const savePendingListingAction = (type, listingId) => {
  const store = storage();
  if (!store || !type || !listingId) {
    return;
  }
  try {
    store.setItem(STORAGE_KEY, JSON.stringify({ type, listingId, savedAt: Date.now() }));
  } catch (e) {
    // Quota or blocked storage. The visitor just has to click again.
  }
};

/**
 * Read and remove the pending action of the given type.
 *
 * Returns its listing id, or null. With `listingId`, only an action for that
 * listing is taken; an action for another listing is left in place for the
 * page it belongs to. An expired entry is removed and ignored.
 */
export const takePendingListingAction = (type, listingId = null) => {
  const pending = read();
  if (!pending) {
    return null;
  }

  const isExpired = !pending.savedAt || Date.now() - pending.savedAt > MAX_AGE_MS;
  if (isExpired) {
    clear();
    return null;
  }

  if (pending.type !== type || (listingId && pending.listingId !== listingId)) {
    return null;
  }

  clear();
  return pending.listingId;
};
